"use client";

import { Tooltip } from "@app-foundation/ui-controls";
import "@app-foundation/ui-controls/styles.css";
import { useEffect, useSyncExternalStore } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { GlobeHemisphereWest, Laptop } from "@phosphor-icons/react";
import { environmentSwitchTarget, LIVE_ORIGIN, LOCAL_SWITCH_COOKIE, LOCAL_SWITCH_PARAM } from "@/lib/environment-switch";

function subscribe(onChange: () => void) {
  document.addEventListener("pointerdown", onChange, true);
  document.addEventListener("focusin", onChange, true);
  window.addEventListener("popstate", onChange);
  window.addEventListener("hashchange", onChange);
  return () => {
    document.removeEventListener("pointerdown", onChange, true);
    document.removeEventListener("focusin", onChange, true);
    window.removeEventListener("popstate", onChange);
    window.removeEventListener("hashchange", onChange);
  };
}

function currentLocation() {
  return window.location.href;
}

export function EnvironmentSwitch() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const location = useSyncExternalStore(subscribe, currentLocation, () => "");
  useEffect(() => {
    if (!location) return;
    const url = new URL(location);
    if (url.origin !== LIVE_ORIGIN || url.searchParams.get(LOCAL_SWITCH_PARAM) !== "1") return;
    // This flag only exposes a navigation shortcut; it grants no preview access.
    document.cookie = `${LOCAL_SWITCH_COOKIE}=1; Path=/; Max-Age=2592000; SameSite=Lax; Secure`;
    if (!document.cookie.split("; ").includes(`${LOCAL_SWITCH_COOKIE}=1`)) return;
    url.searchParams.delete(LOCAL_SWITCH_PARAM);
    router.replace(url.href, { scroll: false });
  }, [location, router]);
  if (!location) return null;
  const current = new URL(location);
  current.pathname = pathname;
  current.search = searchParams.toString();
  const enabledOnLive = document.cookie.split("; ").includes(`${LOCAL_SWITCH_COOKIE}=1`);
  const href = environmentSwitchTarget(current, enabledOnLive) ?? "";
  if (!href) return null;
  const toLive = href.startsWith(LIVE_ORIGIN);
  const label = toLive ? "切换到线上" : "切换到本地";
  return (
    <Tooltip content={label}>{tip => <a {...tip}
      href={href}
      aria-label={label}
      className="fixed bottom-5 right-5 z-[70] grid size-11 place-items-center rounded-full border border-[var(--color-line-strong)] bg-[var(--color-surface-2)] text-[var(--color-paper)] shadow-lg transition-colors hover:bg-[var(--color-surface-3)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
    >
      {toLive ? <GlobeHemisphereWest size={19} aria-hidden="true" /> : <Laptop size={19} aria-hidden="true" />}
    </a>}</Tooltip>
  );
}
